/**
 * Location inventory status block (main-product "location_inventory" block).
 *
 * Per-location quantities are server-rendered into data-availability
 * ("locationId:qty,locationId:qty") from the Multi-Location Inventory app's
 * variant metafield. This element:
 *  - fills the "In stock at <store>" line for the visitor's selected store
 *    (from StoreSelectorDrawer, updating on store-selector:change);
 *  - stamps an In stock / Low stock / Out of stock line onto each location
 *    card in the store selector drawer;
 *  - refreshes itself from the variantChange pub/sub event, whose payload
 *    carries the re-rendered section HTML for the newly selected variant.
 *
 * Statuses are binary-ish snapshots (the app re-syncs metafields
 * periodically), so quantities are never shown — only stock levels.
 */
class LocationInventory extends HTMLElement {
  connectedCallback() {
    this.availability = this.parseAvailability();

    this.onStoreChange = () => this.renderSelected();
    document.addEventListener('store-selector:change', this.onStoreChange);

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, ({ data: { html } }) => {
        this.refreshFrom(html);
      });
    }

    this.querySelector('[data-location-drawer-open]')?.addEventListener('click', (event) => {
      document.querySelector('store-selector-drawer')?.open(event.currentTarget);
    });

    this.renderSelected();
    this.decorateDrawer();
  }

  disconnectedCallback() {
    document.removeEventListener('store-selector:change', this.onStoreChange);
    this.variantChangeUnsubscriber?.();
  }

  parseAvailability() {
    const map = {};
    (this.dataset.availability || '').split(',').forEach((pair) => {
      const [id, qty] = pair.split(':');
      if (id) map[id.trim()] = Number(qty);
    });
    return map;
  }

  // undefined → no data for this location; otherwise a stock level.
  statusFor(quantity) {
    if (quantity === undefined || Number.isNaN(quantity)) return undefined;
    if (quantity <= 0) return 'out';
    if (quantity <= (Number(this.dataset.threshold) || 0)) return 'low';
    return 'in';
  }

  // Re-read data from the section HTML fetched on variant change.
  refreshFrom(html) {
    const fresh = html.querySelector('location-inventory');
    if (!fresh) return;
    this.dataset.availability = fresh.dataset.availability || '';
    this.availability = this.parseAvailability();
    // Whether the new variant can be bought online decides if a store is named.
    if (fresh.dataset.allowStore) this.dataset.allowStore = fresh.dataset.allowStore;
    else delete this.dataset.allowStore;
    const freshOnline = fresh.querySelector('[data-online-line]');
    const online = this.querySelector('[data-online-line]');
    if (freshOnline && online) {
      online.innerHTML = freshOnline.innerHTML;
      online.classList.toggle('visibility-hidden', freshOnline.classList.contains('visibility-hidden'));
    }
    this.renderSelected();
    this.decorateDrawer();
  }

  // Appends the selected store onto the online status line, e.g.
  // "In stock online and at Kenaston Blvd".
  renderSelected() {
    const suffix = this.querySelector('[data-store-suffix]');
    if (!suffix) return;
    suffix.textContent = '';

    // Nothing to add when the item can't be bought online anyway.
    if (!this.dataset.allowStore) return;

    const selected =
      typeof StoreSelectorDrawer !== 'undefined' && StoreSelectorDrawer.getSelected
        ? StoreSelectorDrawer.getSelected()
        : null;
    const status = selected?.locationId ? this.statusFor(this.availability[selected.locationId]) : undefined;
    if (!status) return;

    const template = status === 'out' ? this.dataset.textNotAt : this.dataset.textAndAt;
    suffix.textContent = ` ${template.replace('[name]', selected.name)}`;
  }

  decorateDrawer() {
    document.querySelectorAll('store-selector-drawer [data-store-card]').forEach((card) => {
      const el = card.querySelector('[data-store-stock]');
      if (!el) return;
      const status = card.dataset.locationId ? this.statusFor(this.availability[card.dataset.locationId]) : undefined;
      el.textContent = '';
      if (!status) {
        el.hidden = true;
        return;
      }
      const dot = document.createElement('span');
      dot.className = `store-selector__status-dot store-selector__status-dot--stock-${status}`;
      const textKey = { in: 'textStockIn', low: 'textStockLow', out: 'textStockOut' }[status];
      el.append(dot, document.createTextNode(this.dataset[textKey]));
      el.hidden = false;
    });
  }
}

customElements.define('location-inventory', LocationInventory);
